/*
 * Copyright (C) 2023 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import type {
  AssessmentRequest,
  GraphQLAssesmentRequest,
  GraphQLAssignment,
  GraphQLModuleItemsNode,
  GraphQLResponse,
  StudentViewPeerReviewsAssignment,
} from '../types'
import ASSIGNMENT_QUERY from '../graphql/Queries'
import $ from 'jquery'
import {createClient} from '@canvas/apollo-v3'
import type {ReactElement} from 'react'

export function formatAssessmentRequest({
  anonymizedUser,
  anonymousId,
  available,
  createdAt,
  user,
  workflowState,
}: GraphQLAssesmentRequest): AssessmentRequest {
  const {id: user_id, name: user_name} = user

  return {
    anonymous_id: anonymousId,
    available,
    createdAt,
    user_id,
    user_name: anonymizedUser?.name ?? user_name,
    workflow_state: workflowState,
  }
}

export function formatAssignment(
  {
    _id: assignmentId,
    assessmentRequestsForCurrentUser: assessmentRequests = [],
    name,
    peerReviews,
    peerReviewSubAssignment,
  }: GraphQLAssignment,
  moduleId: string,
): {
  assessmentRequests: GraphQLAssesmentRequest[] | []
  assignmentId: string
  studentViewPeerReviewsAssignment: StudentViewPeerReviewsAssignment
} | null {
  if (
    (assessmentRequests.length === 0 &&
      !(ENV.FEATURES.peer_review_allocation_and_grading && peerReviewSubAssignment)) ||
    ENV.course_id == null
  )
    return null

  const container: Element | undefined = $(
    `#module_student_view_peer_reviews_${assignmentId}_${moduleId}`,
  )[0]

  const {anonymousReviews, count, pointsPossible} = peerReviews
  const peerReviewDueAt = peerReviewSubAssignment?.dueAt ?? null

  return {
    studentViewPeerReviewsAssignment: {
      [assignmentId]: {
        assignment: {
          anonymous_peer_reviews: anonymousReviews,
          assessment_requests: [],
          course_id: ENV.course_id,
          id: assignmentId,
          name,
          peer_review_count: count,
          peer_review_points_possible: pointsPossible,
          peer_review_due_at: peerReviewDueAt,
          peer_review_sub_assignment: peerReviewSubAssignment ?? null,
        },
        container,
      },
    },
    assessmentRequests,
    assignmentId,
  }
}

export function compareByCreatedAt(a: AssessmentRequest, b: AssessmentRequest) {
  const dateA = new Date(a.createdAt)
  const dateB = new Date(b.createdAt)

  return dateA.getTime() - dateB.getTime()
}

export function formatGraphqlModuleNodes(
  graphqlModuleItemNodes: GraphQLModuleItemsNode[],
): [string, StudentViewPeerReviewsAssignment][] {
  const studentViewPeerReviewsAssignments: StudentViewPeerReviewsAssignment[] = []

  const filteredNodes = graphqlModuleItemNodes.filter(
    node => node && node.moduleItems && node.moduleItems.length > 0,
  )

  filteredNodes.forEach(({id: moduleId, moduleItems}) => {
    moduleItems.forEach(({content: assignment}) => {
      const formattedAssignment = formatAssignment(assignment, moduleId)

      if (!formattedAssignment) return

      const {studentViewPeerReviewsAssignment, assessmentRequests, assignmentId} =
        formattedAssignment

      if (!assessmentRequests.length && !ENV.FEATURES.peer_review_allocation_and_grading) return

      const formattedAssessmentRequests = assessmentRequests.map(formatAssessmentRequest)
      studentViewPeerReviewsAssignment[assignmentId].assignment.assessment_requests =
        formattedAssessmentRequests.sort(compareByCreatedAt)

      studentViewPeerReviewsAssignments.push(studentViewPeerReviewsAssignment)
    })
  })

  return Object.entries(studentViewPeerReviewsAssignments)
}

export async function getAssignments(courseId: string): Promise<Array<GraphQLModuleItemsNode>> {
  const client = createClient()
  const allNodes: GraphQLModuleItemsNode[] = []
  let cursor: string | null = null

  do {
    const response: GraphQLResponse = await client.query({
      query: ASSIGNMENT_QUERY,
      variables: {courseId, cursor},
    })

    const queryResponse = response?.data?.course
    if (!queryResponse) {
      break
    }

    const connection = queryResponse.modulesConnection
    if (connection?.nodes) {
      allNodes.push(...connection.nodes)
    }

    cursor = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null
  } while (cursor)

  return allNodes
}
